import { useCallback, useEffect, useMemo, useRef, useState } from "react"

import { Col, Empty, Flex, Row, Spin, Typography } from "antd"
import { useTranslation } from "react-i18next"
import { connect, useSelector } from "react-redux"

import DashboardCard from "../../../Components/DashboardCard"
import { DashboardFiltersBlock } from "../../../Components/Filters/DashboardFiltersBlock/DashboardFiltersBlock"
import DropdownBlockCheckbox from "../../../Components/Filters/DropdownBlockCheckbox"
import {
  countries,
  countryCodesByCountry,
  countryFlagsByCountryCode
} from "../../../Components/Filters/filterOptionsData"
import Globe from "../../../Components/Globe"
import Private from "../../../Components/Private"
import SearchComponent from "../../../Components/Search"
import SelectComponent from "../../../Components/Select"
import TableComponent from "../../../Components/Table"
import Images from "../../../Images"
import { getDates } from "../../../Utils/utils"
import PercentageBar from "../components/ByCountryBlock/PercentageBar"
import HealthMobilePagination from "../components/HealthMobilePagination"
import HealthSubpageHeader from "../components/HealthSubpageHeader"
import HealthSubpageLayout from "../components/HealthSubpageLayout"
import {
  formatAmount,
  formatCount,
  getColumnsByCountry,
  getRowPercentage
} from "../components/Table/columns"
import { useHealthFilters, useHealthSubpage, useTableSettings } from "../hooks"
import { healthSetProp } from "../redux/health.slice"
import { getDataByCountry } from "../redux/health.thunks"
import { useBusinessAccountId } from "../VAMP/hooks/useBusinessAccountId"

import "../components/ByCountryBlock/styles.less"

const { Text, Title } = Typography

const pageSize = 50

const PERCENT_FORMAT = { minimumFractionDigits: 2, maximumFractionDigits: 2 }

const OTHER_COUNTRY_CODE = "OTHER"

const MIN_TRANSACTIONS_FOR_RATE = 50

const countryMapValue = (row) => {
  if ((row?.transactions_count ?? 0) < MIN_TRANSACTIONS_FOR_RATE) return null
  return row?.chargeback_rate ?? null
}

const BreakdownByCountryPage = ({
  filters,
  selectCountries,
  dataByCountryFetching,
  dataByCountry,
  theme,
  ...props
}) => {
  const { t } = useTranslation()
  const isMobile = useSelector((state) => state.nav.isMobile)
  const businessId = useBusinessAccountId()
  const { count, result } = dataByCountry
  const [mobileColumn, setMobileColumn] = useState("transactions")
  const { dataSource } = useSelector((state) => state.health)

  // Custom hooks for common logic
  const { pageNumber, ordering, handleChangeTable, setPageNumber } = useHealthSubpage(pageSize)
  const { handleChangeFilter } = useHealthFilters(props.changeFilters)

  // Data fetching
  const helpRef = useRef({ firstLoading: true })

  const countryCodesFilter = useMemo(() => {
    if (selectCountries === "all" || !selectCountries || !selectCountries.length) {
      return null
    }
    return selectCountries
      ?.map((countryName) => countryCodesByCountry[countryName])
      ?.filter((code) => code)
      ?.join(",")
  }, [selectCountries])

  useEffect(() => {
    if (filters?.period) {
      const period_query = getDates(filters.period)
      props.getDataByCountry({
        page: pageNumber,
        page_size: pageSize,
        ordering,
        business_account: businessId,
        country: countryCodesFilter,
        source: dataSource ?? "stripe",
        ...period_query
      })

      if (helpRef.current.firstLoading) {
        helpRef.current.firstLoading = false
      }
    }
  }, [
    filters?.period,
    businessId,
    ordering,
    pageNumber,
    pageSize,
    props.getDataByCountry,
    countryCodesFilter,
    dataSource
  ])

  const mapData = useMemo(
    () =>
      (result ?? []).reduce((acc, row) => {
        const code = row?.country_code?.toUpperCase()
        if (!code || code === OTHER_COUNTRY_CODE) return acc
        const value = countryMapValue(row)
        if (value != null) acc.push({ code, name: row.country, value, rate: row.chargeback_rate })
        return acc
      }, []),
    [result]
  )

  const [countrySearch, setCountrySearch] = useState("")

  const filteredResult = useMemo(() => {
    const term = countrySearch.trim().toLowerCase()
    if (!term) return result ?? []
    return (result ?? []).filter((row) => (row?.country ?? "").toLowerCase().includes(term))
  }, [result, countrySearch])

  const handleSearchCountry = useCallback((event) => {
    setCountrySearch(event.target.value)
    setPageNumber(1)
  }, [])

  const cards = useMemo(() => {
    const summary = dataByCountry?.summary ?? {}
    return [
      {
        title: "Total transactions",
        value: summary.transactions_count ?? 0,
        icon: "credit_card"
      },
      {
        title: "Total chargebacks",
        value: summary.chargebacks_count ?? 0,
        icon: "alert_triangle_error"
      },
      {
        title: "Chargeback rate",
        value: summary.chargeback_rate ?? 0,
        suffix: "%",
        format: PERCENT_FORMAT,
        icon: "stats_bar_chart"
      },
      {
        title: "Prevention rate",
        value: summary.prevention_rate ?? 0,
        suffix: "%",
        format: PERCENT_FORMAT,
        icon: "bh_vamp_check"
      }
    ]
  }, [dataByCountry])

  // Memoized data
  const columns = useMemo(() => getColumnsByCountry({ t, withSort: true }), [t])

  const tableSettings = useTableSettings(
    countrySearch ? filteredResult.length : count,
    pageNumber,
    pageSize
  )

  const mobileColumnOptions = useMemo(
    () => [
      { label: t("Transactions"), value: "transactions" },
      { label: t("Chargebacks"), value: "chargebacks" },
      { label: t("Alerts"), value: "alerts" },
      { label: t("Chargeback rate"), value: "chargeback_rate" },
      { label: t("Prevention rate"), value: "prevention_rate" }
    ],
    [t]
  )

  const selectedMobileColumnLabel =
    mobileColumnOptions.find((option) => option.value === mobileColumn)?.label || t("Column")

  // Custom row component with percentage bar
  const CustomRow = (props) => {
    const percentage = props["data-percentage"] || 0
    return (
      <PercentageBar percentage={percentage} theme={theme}>
        <tr {...props} />
      </PercentageBar>
    )
  }

  const tableComponents = useMemo(
    () => ({
      body: {
        row: CustomRow
      }
    }),
    [result, theme]
  )

  const handleChangeFilterCountries = useCallback((newFilters) => {
    props.changeCounties(newFilters?.["country"])
    setPageNumber(1)
  }, [])

  const renderMobileValueCell = useCallback(
    (record) => {
      switch (mobileColumn) {
        case "transactions":
          return (
            <Flex gap={2} vertical={true} align="flex-end">
              <Text strong>{formatCount(record?.transactions_count)}</Text>
              <Text type="secondary" strong>
                <Private>{formatAmount(record?.transactions_amount)}</Private>
              </Text>
            </Flex>
          )
        case "chargebacks":
          return (
            <Flex gap={2} vertical={true} align="flex-end">
              <Text strong>{formatCount(record?.chargebacks_count)}</Text>
              <Text type="secondary" strong>
                <Private>{formatAmount(record?.chargebacks_amount)}</Private>
              </Text>
            </Flex>
          )
        case "alerts":
          return (
            <Flex gap={2} vertical={true} align="flex-end">
              <Text strong>{formatCount(record?.alerts_count)}</Text>
              <Text type="secondary" strong>
                <Private>{formatAmount(record?.alerts_amount)}</Private>
              </Text>
            </Flex>
          )
        case "chargeback_rate": {
          const chargebackRate = record?.chargeback_rate || 0
          const chargebackRateWithoutUs = record?.chargeback_rate_without_us || 0
          return (
            <Flex gap={2} vertical={true} align="flex-end">
              <Text strong>{chargebackRate}%</Text>
              <Text type="secondary" strong>
                {chargebackRateWithoutUs}% {t("without us")}
              </Text>
            </Flex>
          )
        }
        case "prevention_rate":
          return <Text strong>{record?.prevention_rate || 0}%</Text>
        default:
          return null
      }
    },
    [mobileColumn, t]
  )

  return (
    <HealthSubpageLayout backText={t("Back to Health")} isMobile={isMobile}>
      {isMobile ? (
        <Flex vertical gap={12} className="by-country-subpage-mobile__header-block">
          <HealthSubpageHeader title={t("Breakdown by Country")} />
          <DashboardFiltersBlock
            handleChangeFilter={handleChangeFilter}
            filters={filters}
            onlyPeriod={true}
          />
          <DropdownBlockCheckbox
            classNameWrapper="health-dropdown-block-checkbox by-country-subpage-mobile__dropdown"
            id="country"
            withSearch={true}
            setDropdownFilters={handleChangeFilterCountries}
            dropdownFilters={selectCountries}
            allBtnText="All"
            label="Country"
            data={countries}
            newMarkStyle={true}
          />
          <SelectComponent
            size="large"
            value={mobileColumn}
            onChange={setMobileColumn}
            options={mobileColumnOptions}
            style={{ width: "100%" }}
          />
        </Flex>
      ) : (
        <HealthSubpageHeader title={t("Breakdown by Country")}>
          <DropdownBlockCheckbox
            classNameWrapper="health-dropdown-block-checkbox"
            id="country"
            withSearch={true}
            setDropdownFilters={handleChangeFilterCountries}
            dropdownFilters={selectCountries}
            allBtnText="All"
            label="Country"
            data={countries}
            newMarkStyle={true}
          />
          <DashboardFiltersBlock
            handleChangeFilter={handleChangeFilter}
            filters={filters}
            onlyPeriod={true}
          />
        </HealthSubpageHeader>
      )}

      <div className="globe-hero">
        {/* Same call as the dashboard's Alerts by Country: Globe.less handles the mobile
            layout itself (shorter stage, controls below the sphere, cards moved above it via
            `order: -1`), so the globe renders on both breakpoints. */}
        <Globe
          data={mapData}
          theme={theme}
          isMobile={isMobile}
          intensityScale="linear"
          ariaLabel={t("Breakdown by Country")}
          legendAriaLabel={t("Darker means a higher chargeback rate")}
          renderTooltipValue={(row) =>
            `${Number(row.rate ?? 0).toFixed(2)}% ${t("chargeback rate")}`
          }
        />

        <Row gutter={isMobile ? [12, 12] : [24, 24]} className="globe-cards">
          {cards.map((card) => (
            <Col key={card.title} xs={24} sm={12} lg={6}>
              <DashboardCard data={card} theme={theme} />
            </Col>
          ))}
        </Row>
      </div>

      <Flex vertical gap={16} className="breakdown-table-header">
        <Title level={5} style={{ marginBottom: 0 }}>
          {t("Country breakdown")}
        </Title>
        <div className="breakdown-search">
          <SearchComponent
            handleSearch={handleSearchCountry}
            value={countrySearch}
            placeholder={t("Search country")}
          />
        </div>
      </Flex>

      {isMobile ? (
        <div>
          <Flex vertical gap={12} className="by-country-subpage-mobile">
            <div className="by-country-subpage-mobile__header">
              <Text type="secondary">{t("Country")}</Text>
              <Text type="secondary" className="by-country-subpage-mobile__header-right">
                {selectedMobileColumnLabel}
              </Text>
            </div>

            {dataByCountryFetching ? (
              <Flex justify="center" style={{ padding: "24px 0" }}>
                <Spin spinning={true} />
              </Flex>
            ) : filteredResult?.length ? (
              filteredResult.map((record, index) => {
                const code = record?.country_code?.toUpperCase()
                const image = countryFlagsByCountryCode?.[code] || null
                const isLastRow = index === filteredResult.length - 1

                return (
                  <div
                    key={record.country}
                    className={`by-country-subpage-mobile__row${
                      isLastRow ? " by-country-subpage-mobile__row--last" : ""
                    }`}
                  >
                    <Flex gap={8} align="center" className="by-country-subpage-mobile__left">
                      {image && <img src={image} alt={record.country} width={20} height={20} />}
                      <Text strong>{record.country}</Text>
                    </Flex>
                    <div className="by-country-subpage-mobile__right">
                      {renderMobileValueCell(record)}
                    </div>
                  </div>
                )
              })
            ) : (
              <div className="by-country-mobile-table__empty">
                <Empty
                  image={Images.no_data}
                  imageStyle={{ width: 64, height: 40 }}
                  description={countrySearch ? t("No countries found") : t("No Data")}
                />
              </div>
            )}
          </Flex>

          <HealthMobilePagination
            current={pageNumber}
            pageSize={pageSize}
            total={(countrySearch ? filteredResult.length : count) || 0}
            onChange={setPageNumber}
          />
        </div>
      ) : (
        <TableComponent
          loading={dataByCountryFetching}
          dataSource={filteredResult}
          columns={columns}
          scroll={tableSettings.scroll}
          pagination={tableSettings.pagination}
          onChange={handleChangeTable}
          rowKey="country"
          components={tableComponents}
          onRow={(record, index) => ({
            "data-percentage": getRowPercentage(record, result)
          })}
        />
      )}
    </HealthSubpageLayout>
  )
}

const mapStateToProps = (state) => ({
  filters: state.health.filters,
  dataByCountryFetching: state.health.dataByCountryFetching,
  dataByCountry: state.health.dataByCountry,
  selectCountries: state.health.selectCountries,
  theme: state.nav.theme
})

const mapDispatchToProps = (dispatch) => ({
  getDataByCountry: (params) => dispatch(getDataByCountry(params)),
  changeFilters: (filters) => dispatch(healthSetProp({ key: "filters", value: filters })),
  changeCounties: (counties) => dispatch(healthSetProp({ key: "selectCountries", value: counties }))
})

export default connect(mapStateToProps, mapDispatchToProps)(BreakdownByCountryPage)
